// src/pages/DailyLogPage.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  setDoc,
  serverTimestamp,
  orderBy,
  getDocs,
  getDoc, // ✅ added
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import styles from '../styles/DailyLogPage.module.css';
import { formatDate } from '../utils/styleUtils';
import { FiEdit3, FiInbox } from 'react-icons/fi';

// --- Helpers ---
const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
};

// Helper to get today's date string in YYYY-MM-DD format
const getTodayDateString = () => toYMD(new Date());

// Check vacation for a specific date with index-safe fallback
const isOnVacationForDate = async (uid, ymd) => {
  if (!uid || !ymd) return false;
  try {
    // Preferred query (may need composite index attendance(userId, date))
    const q1 = query(
      collection(db, 'attendance'),
      where('userId', '==', uid),
      where('date', '==', ymd)
    );
    const snap = await getDocs(q1);
    let onVac = false;
    snap.forEach((d) => {
      const data = d.data() || {};
      const status = (data.status || '').toLowerCase();
      if (status === 'vacation' || data?.vacation?.type || data?.vacation?.isVacation) {
        onVac = true;
      }
    });
    return onVac;
  } catch (err) {
    // Fallback if index missing: fetch by userId and filter locally
    try {
      const q2 = query(collection(db, 'attendance'), where('userId', '==', uid));
      const snap2 = await getDocs(q2);
      let onVac = false;
      snap2.forEach((d) => {
        const data = d.data() || {};
        if (data.date === ymd) {
          const status = (data.status || '').toLowerCase();
          if (status === 'vacation' || data?.vacation?.type || data?.vacation?.isVacation) {
            onVac = true;
          }
        }
      });
      return onVac;
    } catch {
      return false; // if we can't verify, don't block other logic
    }
  }
};

const DailyLogPage = () => {
  const { currentUser, userRole } = useAuth();
  const location = useLocation();

  const [logText, setLogText] = useState('');
  const [pastLogs, setPastLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [highlightedLogId, setHighlightedLogId] = useState(null);
  const [pastLogFilter, setPastLogFilter] = useState('last7'); // 'today', 'yesterday', 'last7'

  // Vacation state for *today*
  const [isOnVacationToday, setIsOnVacationToday] = useState(false);
  const [vacationNote, setVacationNote] = useState('');

  const isMounted = useRef(true);
  const messageTimeoutRef = useRef(null);
  const autoZeroDoneRef = useRef(false); // ✅ ensure one pass per mount

  const todayString = useMemo(() => getTodayDateString(), []);

  const todaysLog = useMemo(() => {
    return pastLogs.find((log) => log.logDate === todayString);
  }, [pastLogs, todayString]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (successMessage || error) {
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
      messageTimeoutRef.current = setTimeout(() => {
        if (isMounted.current) {
          setSuccessMessage('');
          setError('');
        }
      }, 4000);
    }
    return () => {
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    };
  }, [successMessage, error]);

  // 🔎 Check if TODAY is a vacation day for this user
  useEffect(() => {
    if (!currentUser?.uid) return;

    let cancelled = false;
    const run = async () => {
      try {
        const q1 = query(
          collection(db, 'attendance'),
          where('userId', '==', currentUser.uid),
          where('date', '==', todayString)
        );
        const snap = await getDocs(q1);

        let isVacation = false;
        let note = '';
        snap.forEach((d) => {
          const data = d.data() || {};
          const status = (data.status || '').toLowerCase();
          if (status === 'vacation' || data.vacation?.type || data.vacation?.isVacation) {
            isVacation = true;
            note = data.vacation?.note || '';
          }
        });

        if (!cancelled && isMounted.current) {
          setIsOnVacationToday(isVacation);
          setVacationNote(note);
        }
      } catch (err) {
        // Fallback if index is missing: fetch by userId only and filter client-side
        try {
          const q2 = query(collection(db, 'attendance'), where('userId', '==', currentUser.uid));
          const snap2 = await getDocs(q2);
          let isVacation = false;
          let note = '';
          snap2.forEach((d) => {
            const data = d.data() || {};
            if (data.date === todayString) {
              const status = (data.status || '').toLowerCase();
              if (status === 'vacation' || data.vacation?.type || data.vacation?.isVacation) {
                isVacation = true;
                note = data.vacation?.note || '';
              }
            }
          });
          if (!cancelled && isMounted.current) {
            setIsOnVacationToday(isVacation);
            setVacationNote(note);
          }
        } catch (err2) {
          console.warn('Vacation check fallback failed:', err2);
          if (!cancelled && isMounted.current) {
            setIsOnVacationToday(false); // default to not-on-vacation if uncertain
          }
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [currentUser, todayString]);

  // Live fetch user's logs
  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');

    const logsQuery = query(
      collection(db, 'dailyLogs'),
      where('userId', '==', currentUser.uid),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(
      logsQuery,
      (snapshot) => {
        if (!isMounted.current) return;
        const logsData = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
          timestamp: docSnap.data().timestamp?.toDate(),
        }));
        setPastLogs(logsData);

        // Only prefill the textarea with today's log if not on vacation
        if (!isOnVacationToday) {
          const todayEntry = logsData.find((log) => log.logDate === todayString);
          setLogText(todayEntry?.logText || '');
        } else {
          setLogText(''); // ensure it's empty on vacation
        }

        setLoading(false);
      },
      (err) => {
        console.error('Firebase Snapshot Error:', err);
        if (isMounted.current) {
          if (err.code === 'failed-precondition') {
            setError(
              'Error: A database index is required to view logs. Please contact an administrator.'
            );
          } else {
            setError('Failed to load your work logs. Please try again later.');
          }
          setLoading(false);
        }
      }
    );

    return () => unsubscribe();
  }, [currentUser, todayString, isOnVacationToday]);

  // ✅ NEW: Auto 0-rate missing logs for the last two days (if not vacation)
  useEffect(() => {
    const runAutoZero = async () => {
      if (autoZeroDoneRef.current) return;
      if (!currentUser?.uid || !userRole) return;

      try {
        const today = new Date();
        const targets = [];
        const y1 = new Date(today); y1.setDate(today.getDate() - 1);
        const y2 = new Date(today); y2.setDate(today.getDate() - 2);
        targets.push(toYMD(y1), toYMD(y2));

        for (const ymd of targets) {
          const logId = `${currentUser.uid}_${ymd}`;
          const logRef = doc(db, 'dailyLogs', logId);
          const existing = await getDoc(logRef);
          if (existing.exists()) continue; // already has a log (manual or previous auto)

          // skip if on vacation for that date
          const onVac = await isOnVacationForDate(currentUser.uid, ymd);
          if (onVac) continue;

          // Create an auto 0-rated log for that day
          const middayUTC = new Date(`${ymd}T12:00:00Z`);
          await setDoc(logRef, {
            logText: '',
            logDate: ymd,
            userId: currentUser.uid,
            userName: currentUser.displayName || currentUser.email,
            userRole: userRole || null,

            // timestamps (midday for stable local filters)
            timestamp: middayUTC,
            createdAt: middayUTC,
            updatedAt: serverTimestamp(),

            // Auto-rating metadata
            rating: 0,
            managerNotes: 'you didnt add your work log so you done nothing',
            ratedByName: 'System',
            ratedByUid: 'system',
            autoRated: true,
          });
        }
      } catch (e) {
        console.warn('Auto zero-rate pass encountered an issue:', e);
        // Non-blocking
      } finally {
        autoZeroDoneRef.current = true;
      }
    };

    runAutoZero();
  }, [currentUser, userRole]);

  // Effect to scroll to and highlight a log from a notification link
  useEffect(() => {
    if (pastLogs.length > 0 && location.hash) {
      const logIdFromHash = location.hash.substring(1);
      const logElement = document.getElementById(logIdFromHash);

      if (logElement) {
        logElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedLogId(logIdFromHash);

        const timer = setTimeout(() => {
          if (isMounted.current) setHighlightedLogId(null);
        }, 3000);

        return () => clearTimeout(timer);
      }
    }
  }, [pastLogs, location.hash]);

  const handleSaveLog = async (e) => {
    e.preventDefault();

    if (isOnVacationToday) {
      setError("You're on vacation today — no log is required.");
      return;
    }

    if (!logText.trim()) {
      setError('Log entry cannot be empty.');
      return;
    }
    if (!currentUser || !userRole) {
      setError('You must be logged in to save a log.');
      return;
    }
    if (isMounted.current) {
      setIsProcessing(true);
      setError('');
      setSuccessMessage('');
    }

    const logId = `${currentUser.uid}_${todayString}`;
    const logRef = doc(db, 'dailyLogs', logId);
    const dataToSave = {
      logText: logText.trim(),
      logDate: todayString,
      userId: currentUser.uid,
      userName: currentUser.displayName || currentUser.email,
      userRole: userRole || null,
      updatedAt: serverTimestamp(),
    };

    try {
      if (todaysLog) {
        await setDoc(logRef, dataToSave, { merge: true });
      } else {
        dataToSave.createdAt = serverTimestamp();
        dataToSave.timestamp = serverTimestamp();
        await setDoc(logRef, dataToSave);
      }
      if (isMounted.current) setSuccessMessage("Today's log has been saved successfully!");
    } catch (err) {
      console.error('Error saving log:', err);
      if (isMounted.current) setError('Failed to save your log. Please try again.');
    } finally {
      if (isMounted.current) setIsProcessing(false);
    }
  };

  // Filter past logs based on the selected filter
  const filteredPastLogs = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (pastLogFilter) {
      case 'today':
        return pastLogs.filter((log) => log.timestamp && log.timestamp >= todayStart);

      case 'yesterday': {
        const yesterdayStart = new Date(todayStart);
        yesterdayStart.setDate(todayStart.getDate() - 1);
        const yesterdayEnd = new Date(todayStart);
        yesterdayEnd.setMilliseconds(-1);
        return pastLogs.filter(
          (log) =>
            log.timestamp && log.timestamp >= yesterdayStart && log.timestamp <= yesterdayEnd
        );
      }

      case 'last7': {
        const sevenDaysAgoStart = new Date(todayStart);
        sevenDaysAgoStart.setDate(todayStart.getDate() - 6);
        return pastLogs.filter((log) => log.timestamp && log.timestamp >= sevenDaysAgoStart);
      }

      default:
        return pastLogs;
    }
  }, [pastLogs, pastLogFilter]);

  if (loading) {
    return <div className={styles.loadingIndicator}>Loading your work logs...</div>;
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>
          <FiEdit3 className={styles.titleIcon} /> My Daily Work Log
        </h1>
        <p className={styles.subtitle}>Record what you worked on each day and review manager feedback.</p>
      </div>

      {/* If on vacation today, show message instead of form */}
      {isOnVacationToday ? (
        <div className={styles.vacationMessage}>
          <strong>Vacation Day:</strong> You’re marked as on vacation today
          {vacationNote ? ` — ${vacationNote}` : ''}. No work log is required.
        </div>
      ) : (
        <div className={styles.todayLogSection}>
          <h3>{todaysLog ? "Edit Today's Log" : "Add Today's Log"} ({formatDate(new Date())})</h3>
          <form onSubmit={handleSaveLog}>
            <textarea
              className={styles.logTextarea}
              value={logText}
              onChange={(e) => setLogText(e.target.value)}
              placeholder="What did you work on today? List your tasks, accomplishments, and any challenges."
              rows="8"
              disabled={isProcessing}
            />
            <div className={styles.formActions}>
              {error && <p className={styles.errorMessage}>{error}</p>}
              {successMessage && <p className={styles.successMessage}>{successMessage}</p>}
              <button type="submit" className={styles.saveButton} disabled={isProcessing}>
                {isProcessing ? 'Saving...' : todaysLog ? 'Update Log' : 'Save Log'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className={styles.pastLogsSection}>
        <div className={styles.pastLogsHeader}>
          <h3>Your Past Entries</h3>
          <div className={styles.filterButtons}>
            <button
              className={pastLogFilter === 'today' ? styles.activeFilter : ''}
              onClick={() => setPastLogFilter('today')}
            >
              Today
            </button>
            <button
              className={pastLogFilter === 'yesterday' ? styles.activeFilter : ''}
              onClick={() => setPastLogFilter('yesterday')}
            >
              Yesterday
            </button>
            <button
              className={pastLogFilter === 'last7' ? styles.activeFilter : ''}
              onClick={() => setPastLogFilter('last7')}
            >
              Last 7 Days
            </button>
          </div>
        </div>

        {!loading && error && <p className={styles.errorMessage}>{error}</p>}
        {!loading && !error && filteredPastLogs.length === 0 && (
          <div className={styles.emptyState}>
            <FiInbox className={styles.emptyIcon} />
            <p style={{ margin: 0, fontWeight: 700, color: '#374151' }}>
              {pastLogs.length > 0
                ? 'No logs found for this period.'
                : "You haven't submitted any work logs yet."}
            </p>
          </div>
        )}

        {filteredPastLogs.length > 0 && (
          <ul className={styles.logList}>
            {filteredPastLogs.map((log) => (
              <li
                key={log.id}
                id={log.id}
                className={`${styles.logItem} ${
                  highlightedLogId === log.id ? styles.highlighted : ''
                }`}
              >
                <div className={styles.logHeader}>
                  <strong>{formatDate(log.timestamp)}</strong>
                  {typeof log.rating === 'number' && ( // ✅ shows 0 too
                    <span className={`${styles.ratingBadge} ${styles[`rating-${log.rating}`]}`}>
                      Rating: {log.rating}/5
                    </span>
                  )}
                </div>
                <p className={styles.logText}>{log.logText || <em>(No text)</em>}</p>
                {log.managerNotes && (
                  <div className={styles.managerFeedback}>
                    <strong>Feedback from {log.ratedByName || 'Manager'}:</strong>
                    <p>{log.managerNotes}</p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default DailyLogPage;
